<script setup>
// NOTICE: KAPTION-INTEGRATION
// Secure Assignment Mode account settings section (group-visibility feature, Screen 1).
// Master toggle (group-visibility.TOGGLE.1) + cross-team leader switch
// (group-visibility.CROSS_TEAM.1) + the pre-flight blocked state rendered from the
// backend 422 payload (group-visibility.PREFLIGHT.1). Calls AccountAPI directly
// (instead of accounts/update) because the store action wraps errors and would
// discard the structured pre-flight payload.
//
// Pre-flight gate 2 ("no user in more than one restricted team") no longer blocks —
// multi-group membership is legal (group-visibility.MULTI_GROUP.1/.6). Its query is
// kept and rendered here as a NON-BLOCKING "before you turn this on" summary,
// alongside group-visibility.GUARDRAIL.5 (people in both a restricted and an open
// group, and restricted groups with no leader). Removing
// the constraint removed the only moment an admin was ever shown the overlap
// footprint; this panel is what puts it back.
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import AccountAPI from 'dashboard/api/account';
import KaptionGroupVisibilityAPI from 'dashboard/api/kaptionGroupVisibility';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const isEnabled = ref(false);
const crossTeam = ref(false);
const isSubmitting = ref(false);
// Structured pre-flight payload from the backend 422 (Screen 1c blocked state).
// Only gate 1 (leaderless restricted teams) can produce this now.
const blocked = ref(null);
// Non-blocking pre-flight advisory, fetched on mount
// (group-visibility.MULTI_GROUP.6 + group-visibility.GUARDRAIL.5).
const advisory = ref(null);
const advisoryFailed = ref(false);

const multiGroupUsers = computed(() => advisory.value?.multi_group_users || []);
const restrictedAndOpenUsers = computed(
  () => advisory.value?.restricted_and_open_users || []
);
const hasAdvisory = computed(
  () =>
    multiGroupUsers.value.length > 0 || restrictedAndOpenUsers.value.length > 0
);

const isFlagEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_GROUP_VISIBILITY')
);

// Advisory only — a failure here must never interfere with the toggle itself.
const loadAdvisory = async () => {
  try {
    const { data } = await KaptionGroupVisibilityAPI.preflight();
    advisory.value = data;
    advisoryFailed.value = false;
  } catch (error) {
    advisory.value = null;
    advisoryFailed.value = true;
  }
};

// Composed in script rather than interpolated in the template so the list rows
// carry no untranslatable punctuation.
const multiGroupLabel = user => `${user.name} — ${user.team_names.join(', ')}`;
const restrictedAndOpenLabel = user =>
  `${user.name} — ${user.restricted_team_names.join(', ')} + ${user.open_team_names.join(', ')}`;

watch(
  currentAccount,
  () => {
    const settings = currentAccount.value?.settings || {};
    isEnabled.value = Boolean(settings.group_visibility_enabled);
    crossTeam.value = Boolean(settings.group_visibility_leaders_cross_team);
  },
  { deep: true, immediate: true }
);

const persist = async payload => {
  try {
    isSubmitting.value = true;
    await AccountAPI.update('', payload);
    blocked.value = null;
    await store.dispatch('accounts/get');
    loadAdvisory();
    useAlert(t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.API.SUCCESS'));
    return true;
  } catch (error) {
    if (error?.response?.status === 422) {
      blocked.value = error.response.data;
    } else {
      useAlert(t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.API.ERROR'));
    }
    return false;
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  if (isFlagEnabled.value) loadAdvisory();
});

const toggleMode = async () => {
  const turningOn = isEnabled.value; // Switch already flipped the model
  const ok = await persist({ group_visibility_enabled: turningOn });
  if (!ok && turningOn) {
    // Pre-flight blocked the enable — the switch stays OFF (fail closed).
    isEnabled.value = false;
  }
};

const toggleCrossTeam = async () => {
  const requested = crossTeam.value; // Switch already flipped the model
  const ok = await persist({ group_visibility_leaders_cross_team: requested });
  if (!ok) {
    // Persist failed — revert the switch so the UI matches the server (mirrors toggleMode).
    crossTeam.value = !requested;
  }
};
</script>

<template>
  <SectionLayout
    v-if="isFlagEnabled"
    :title="t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.NOTE')"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <Switch
          v-model="isEnabled"
          :disabled="isSubmitting"
          @change="toggleMode"
        />
      </div>
    </template>

    <div class="grid gap-4">
      <div
        v-if="blocked"
        class="rounded-xl border border-n-ruby-8 bg-n-ruby-3 p-4 text-sm text-n-slate-12"
      >
        <h5 class="font-medium text-n-ruby-11 mb-1">
          {{ t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.BLOCKED.TITLE') }}
        </h5>
        <p class="text-n-slate-11 mb-2">
          {{ t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.BLOCKED.BODY') }}
        </p>
        <div v-if="blocked.teams_without_leader?.length" class="mb-2">
          <span class="font-medium">
            {{
              t(
                'GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.BLOCKED.MISSING_LEADERS'
              )
            }}
          </span>
          <ul class="list-disc ltr:ml-5 rtl:mr-5">
            <li v-for="team in blocked.teams_without_leader" :key="team.id">
              {{ team.name }}
            </li>
          </ul>
        </div>
      </div>

      <!-- NOTICE: KAPTION-INTEGRATION — non-blocking pre-flight advisory
           (group-visibility.MULTI_GROUP.6 + group-visibility.GUARDRAIL.5). The repurposed
           gate 2: multi-group membership no longer blocks, but the overlap
           footprint is still the thing an admin most needs to see before switching
           the mode on. Slate, not ruby: nothing here is an error. -->
      <div
        v-if="hasAdvisory"
        class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4 text-sm text-n-slate-12"
        data-testid="group-visibility-advisory"
      >
        <h5 class="font-medium text-n-slate-12 mb-1">
          {{ t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.ADVISORY.TITLE') }}
        </h5>
        <div v-if="multiGroupUsers.length" class="mb-2">
          <span class="font-medium">
            {{
              t(
                'GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.ADVISORY.MULTI_GROUP_USERS'
              )
            }}
          </span>
          <ul class="list-disc ltr:ml-5 rtl:mr-5">
            <li v-for="user in multiGroupUsers" :key="user.user_id">
              {{ multiGroupLabel(user) }}
            </li>
          </ul>
        </div>
        <div v-if="restrictedAndOpenUsers.length" class="mb-2">
          <span class="font-medium">
            {{
              t(
                'GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.ADVISORY.RESTRICTED_AND_OPEN_USERS'
              )
            }}
          </span>
          <ul class="list-disc ltr:ml-5 rtl:mr-5">
            <li v-for="user in restrictedAndOpenUsers" :key="user.user_id">
              {{ restrictedAndOpenLabel(user) }}
            </li>
          </ul>
        </div>
        <p class="text-n-slate-11 text-xs mb-0">
          {{ t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.ADVISORY.NOTE') }}
        </p>
      </div>
      <p v-else-if="advisoryFailed" class="text-xs text-n-slate-11 mb-0">
        {{ t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.ADVISORY.ERROR') }}
      </p>

      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
      >
        <div class="p-3 flex items-center justify-between gap-4">
          <div>
            <span>
              {{
                t(
                  'GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.CROSS_TEAM.LABEL'
                )
              }}
            </span>
            <p class="text-n-slate-11 text-xs mt-1 mb-0">
              {{
                t(
                  'GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.CROSS_TEAM.HELP'
                )
              }}
            </p>
          </div>
          <Switch
            v-model="crossTeam"
            :disabled="!isEnabled || isSubmitting"
            @change="toggleCrossTeam"
          />
        </div>
        <div class="p-3 text-n-slate-11">
          {{ t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.INTAKE_QUEUE') }}
        </div>
        <div class="p-3 text-n-slate-11">
          {{ t('GENERAL_SETTINGS.FORM.SECURE_ASSIGNMENT_MODE.MANAGE_LEADERS') }}
        </div>
      </div>
    </div>
  </SectionLayout>
</template>
